<template>

    <div class="mb-5 draggable rounded bg-white">
        <!-- Group Header. -->
        <div class="flex mb-px" :style="'background-color: '+group.color">
            <div class="w-6 flex-shrink-0 py-1.5 -my-px" :style="'background-color: '+group.color">
                <i class="fa fa-bars text-sm text-gray-400 hover:text-gray-500 cursor-move pt-1 pl-1 handle"
                   v-if="!estimate.readonly"></i>
            </div>
            <!-- Reference -->
            <div class="w-16 flex m-0 -ml-px">
                <page-cell
                    class="text-sm -m-px px-2 py-1.5 border hover:z-0 min-h-[34px]"
                    :style="'background-color: '+group.color+';border-color: '+group.color"
                    :placeholder="group.defaultReference"
                    :disabled="estimate.readonly"
                    :read-only="estimate.readonly"
                    v-model="group.reference"
                    @input="estimate.update()"
                    @change="estimate.addSnapshot()"
                    ref="reference"
                ></page-cell>
            </div>
            <div class="flex-1">
                <div
                    v-if="estimate.readonly"
                    class="text-sm px-2 py-1.5 shadow-none relative hover:z-0 focus:z-30 resize-none font-bold text-gray-900 whitespace-pre-wrap"
                    :style="'background-color: '+group.color+';border-color: '+group.color"
                    v-text="group.name"
                >
                </div>
                <autoresizable-textarea :height="el => (el.scrollHeight + 2) + 'px'" v-else>
                <textarea
                    placeholder="Untitled group..."
                    class="text-sm -ml-px -my-px px-2 py-1.5 shadow-none relative hover:z-0 focus:z-30 resize-none font-bold text-gray-900"
                    :style="'background-color: '+group.color+';border-color: '+group.color"
                    type="text"
                    ref="name"
                    :disabled="estimate.readonly"
                    v-model="group.name"
                    @input="estimate.update()"
                    @change="estimate.addSnapshot()"
                    rows="1"
                ></textarea>
                </autoresizable-textarea>
            </div>
            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px pl-1 pr-1" :style="'background-color: '+group.color">
                <ui-dropdown menu-class="p-3 w-56 z-50" :close-on-mouse-out-scroll="true" :key="clipboardKey">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-tint"
                                          @mouseleave="(e) => {$refs['itemcolormenu'].close(e)}"
                                          @mouseenter="openColorSubmenu"
                                          ref="itemcolor"
                                          :submenu="true"
                                          v-if="!estimate.readonly"
                        >
                            Set group color
                            <div class="fixed" style="width:auto;height:auto;overflow: visible"
                                 :ref="'itemcolorrelative'">
                                <vue-context :ref="'itemcolormenu'" :autoFocus="false"
                                             class="inline pb-0 focus:outline-none focus:ring-0 z-30"
                                             :close-on-click="false" :close-on-scroll="false">
                                    <div :id="'itemcolormenu'"
                                         class="p-3 w-48 z-30 bg-white rounded-lg border border-gray-300 shadow-lg">
                                        <div
                                            v-for="(index,key) in ([0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19])"
                                            class="w-8 inline-block items-center p-1.5 border border-none rounded-lg"
                                            :key="index">
                                            <button
                                                class="w-6 h-6 rounded-lg flex-none mt-0.5 mr-2 inline-block border border-gray-300 hover:border-gray-400"
                                                :style="'background-color: '+color(key)"
                                                @click="setColor(group,color(key))">
                                            </button>
                                        </div>
                                    </div>
                                </vue-context>
                            </div>
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                        <ui-dropdown-item icon="fas fa-cut" @click="cutGroup();close();" v-if="!estimate.readonly">Cut
                            Group
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-copy" @click="copyGroup();close();">Copy Group
                        </ui-dropdown-item>
                        <ui-dropdown-item icon="fas fa-paste"
                                          v-if="!estimate.readonly && (clipboardGroupData[0])"
                                          @click="pasteGroup(clipboardGroupData[0],group);close();">
                            Paste Group
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                        <ui-dropdown-item ref="paste1item"
                                          @mouseleave="closePasteSubMenu(1)"
                                          @mouseenter="openPasteSubMenu(1)"
                                          v-if="!estimate.readonly"
                                          icon="fas fa-clipboard"
                                          :submenu="true"
                        >Library
                            <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                 :ref="'paste1relative'">
                                <vue-context :ref="'paste1menu'" :autoFocus="false"
                                             class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                             :close-on-click="false" :close-on-scroll="false">
                                    <div :id="'paste1menu'"
                                         class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg max-h-96 overflow-x-auto">
                                        <ui-dropdown-item icon="fas fa-plus"
                                                          @click="addToLibrary(group);close();">
                                            Add Group to Library
                                        </ui-dropdown-item>
                                        <ui-dropdown-divider
                                            v-if="!estimate.readonly && (clipboardGroupData.length > 1)"></ui-dropdown-divider>
                                        <ui-dropdown-item icon="fas fa-paste"
                                                          button-class="truncate"
                                                          class="w-full truncate flex"
                                                          v-for="(item,i) in clipboardGroupData"
                                                          :key="'paste1-'+i"
                                                          v-if="i > 0"
                                                          @click="pasteGroup(item.data,group);close();">
                                            <div class="text-xs text-blue-500 truncate">{{ item.name }}</div>
                                        </ui-dropdown-item>
                                    </div>
                                </vue-context>
                            </div>
                        </ui-dropdown-item>
                        <ui-dropdown-divider v-if="!estimate.readonly"></ui-dropdown-divider>
                        <ui-dropdown-item icon="fas fa-trash" danger @click="deleteGroup();close();"
                                          v-if="!estimate.readonly">Delete
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>
            </div>
        </div>

        <!-- Group Items. -->
        <draggable :list="group.items" draggable=".draggable" handle=".handle" group="items"
                   @change="estimate.update();estimate.addSnapshot(); $emit('reflow')">
            <estimate-preliminary-group-item v-for="(item,index) in group.items"
                                             :key="index"
                                             :item="item"
                                             :group="group"
                                             :item-clipboard="[]"
                                             :store="store"
                                             :multimode="multimode"
                                             @reflow="$emit('reflow')"
                                             :clipboard-key="clipboardKey"
                                             :clipboard-group-item-data="clipboardGroupItemData"
                                             :clipboard-group-data="clipboardGroupData"
                                             :estimate="estimate" ref="items"></estimate-preliminary-group-item>
        </draggable>

        <!-- New Group Item. -->
        <div class="flex" v-if="!estimate.readonly">
            <div class="w-6 flex-shrink-0 self-start py-1.5 -my-px border-t">
                <i class="fa fa-plus text-xs text-gray-300 pl-1 pt-1"></i>
            </div>
            <div class="w-16 flex m-0">
                <page-cell
                    class="text-sm -my-px ml-0 px-2 py-1.5 border border-gray-300 bg-white"
                    :placeholder="nextItemReference()"
                    v-model="newItem.reference"
                    @input="addItem('reference')"
                ></page-cell>
            </div>
            <div class="flex-1 -ml-px">
                <input
                    placeholder="Add a new item..."
                    class="text-sm -ml-px -my-px px-2 py-1.5 border border-gray-300 shadow-none relative hover:z-10 focus:z-30 resize-none bg-white"
                    type="text"
                    v-model="newItem.description"
                    @input="addItem('description')"
                >
            </div>
            <div class="w-6 flex-shrink-0 self-start pt-2 -my-px border-t pl-1 pr-1">
                <ui-dropdown menu-class="p-3 w-56" :close-on-mouse-out-scroll="true" :key="clipboardKey"
                             v-if="!estimate.readonly">
                    <template #default="{ toggle }">
                        <button @click="toggle"
                                class="text-gray-300 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:z-10 relative rounded">
                            <i class="fa fa-ellipsis-h"></i>
                        </button>
                    </template>
                    <template #menu="{ close }">
                        <ui-dropdown-item icon="fas fa-paste"
                                          v-if="!estimate.readonly && (clipboardGroupItemData[0])"
                                          @click="pasteItem(clipboardGroupItemData[0],false);close();">
                            Paste Item
                        </ui-dropdown-item>
                        <ui-dropdown-divider
                            v-if="!estimate.readonly && (clipboardGroupItemData[0])"></ui-dropdown-divider>
                        <ui-dropdown-item :ref="'paste'+(3)+'item'"
                                          @mouseleave="closePasteSubMenu(3)"
                                          @mouseenter="openPasteSubMenu(3)"
                                          v-if="!estimate.readonly && (clipboardGroupItemData.length > 1)"
                                          icon="fas fa-clipboard"
                                          :submenu="true"
                        >Library
                            <div class="fixed" style="width:auto;height:auto;overflow: visible;"
                                 :ref="'paste'+(3)+'relative'">
                                <vue-context :ref="'paste'+(3)+'menu'" :autoFocus="false"
                                             class="inline pb-0 focus:outline-none focus:ring-0 z-50"
                                             :close-on-click="false" :close-on-scroll="false">
                                    <div :id="'paste'+(3)+'menu'"
                                         class="p-3 w-72 z-50 bg-white rounded-lg border border-gray-300 shadow-lg max-h-96 overflow-x-auto">
                                        <ui-dropdown-item icon="fas fa-paste"
                                                          button-class="truncate"
                                                          class="w-full truncate flex"
                                                          v-for="(item,i) in clipboardGroupData"
                                                          :key="'paste1-'+i"
                                                          v-if="i > 0"
                                                          @click="pasteGroup(item.data,group);close();">
                                            <div class="text-xs text-blue-500 truncate">{{ item.name }}</div>
                                        </ui-dropdown-item>
                                    </div>
                                </vue-context>
                            </div>
                        </ui-dropdown-item>
                    </template>
                </ui-dropdown>

            </div>
        </div>

        <!-- Total. -->
        <div class="flex text-xs text-gray-500 text-right font-semibold">
            <div class="flex-1 px-2 py-1">Preliminary group Total</div>
            <div class="w-28 -ml-px px-2 py-1">
                {{
                    (group.items.reduce((a, item) => a + (item.total ? item.total : 0), 0)) | toCurrency
                }}
            </div>
            <div class="w-6 flex-shrink-0 ml-2"></div>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'
import {Estimate} from "../../../models";
import {cloneDeep} from "lodash";
import UiDropdownItem from "../../ui/UiDropdownItem.vue";
import remoteStorage from "../../../utils/remoteStorage";
import * as math from "mathjs";
import UiDropdownDivider from "../../ui/UiDropdownDivider.vue";
import {toCurrency} from "../../../utils";
import EstimatePreliminaryGroupItem from "./EstimatePreliminaryGroupItem.vue";
import PageCell from "../../ui/PageCell.vue";
import UiDropdown from "../../ui/UiDropdown.vue";

export default {
    components: {
        UiDropdown,
        PageCell,
        EstimatePreliminaryGroupItem,
        UiDropdownDivider,
        draggable,
        UiDropdownItem,
    },
    props: {
        estimate: Estimate,
        group: Object,
        store: Object,
        multimode: false,
        clipboardKey: Number,
        clipboardGroupData: Array,
        clipboardGroupItemData: Array,
    },
    data() {
        return {
            newItem: {
                reference: null,
                description: null,
            },
            hasNonDefaultMarkup: false,
        }
    },
    methods: {
        closePasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].close()
            } else {
                this.$refs[p].close()
            }
        },
        openPasteSubMenu(num) {
            const p = "paste" + num + "menu"
            if (this.$refs[p][0]) {
                this.$refs[p][0].open(new window.MouseEvent('mouse'))
            } else {
                this.$refs[p].open(new window.MouseEvent('mouse'))
            }
            this.positionPasteMenu(num)
        },
        pasteGroup(group, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'group');
            if (at) {
                let num = this.estimate.preliminaries.indexOf(at);
                this.estimate.preliminaries.splice(num, 0, cloneDeep(group));
            } else {
                this.estimate.preliminaries.push(cloneDeep(group));
            }
            this.estimate.update();
            this.estimate.addSnapshot();
            this.$emit('reflow')
        },
        addToLibrary(group) {
            this.$modals.open(
                'estimate-add-item-to-library',
                {
                    name: (group.name + ' - ' + group.reference + ' (' + toCurrency(group.total) + ')').replace(/(\r\n|\n|\r)/gm, ""),
                    title: `Add Preliminary Group to Library`,
                    button: 'Add Preliminary Group',
                    useGroups: false,
                    handle: (name) => {
                        this.clipboardGroupData.push({
                            name: name,
                            data: cloneDeep(group)
                        })
                        remoteStorage.setItem('estimate-group-clipboard', JSON.stringify(this.clipboardGroupData))
                        remoteStorage.setItem('estimate-clipboard-view', 'group')
                    }
                }
            )
        },
        pasteItem(item, at) {
            remoteStorage.setItem('estimate-clipboard-view', 'groupitem');
            this.group.items.push(cloneDeep(item));
            this.estimate.update();
            this.estimate.addSnapshot();
            this.$emit('reflow')
        },
        positionPasteMenu(num) {
            this.$nextTick(() => {
                if (this.$refs["paste" + num + "relative"][0]) {
                    this.$refs["paste" + num + "relative"][0].setAttribute('style', '')
                } else {
                    this.$refs["paste" + num + "relative"].setAttribute('style', '')
                }
                this.$nextTick(() => {
                    let opener, box
                    if (this.$refs["paste" + num + "item"][0]) {
                        opener = this.$refs["paste" + num + "item"][0].$refs.submenu.getBoundingClientRect()
                    } else {
                        opener = this.$refs["paste" + num + "item"].$refs.submenu.getBoundingClientRect()
                    }
                    if (this.$refs["paste" + num + "menu"][0]) {
                        box = this.$refs["paste" + num + "menu"][0].$el.getBoundingClientRect()
                    } else {
                        box = this.$refs["paste" + num + "menu"].$el.getBoundingClientRect()
                    }
                    let left = Math.min(opener.x - box.x - 10, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 10, window.innerHeight - (box.bottom) - 28);
                    if (this.$refs["paste" + num + "relative"][0]) {
                        this.$refs["paste" + num + "relative"][0].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    } else {
                        this.$refs["paste" + num + "relative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                    }
                })
            })
        },
        color(x) {
            return this.estimate.colors(x);
        },
        setColor(item, color) {
            item.color = color
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        openColorSubmenu(e) {
            this.$refs["itemcolormenu"].open(new window.MouseEvent('mouse'))
            this.$nextTick(() => {
                this.$refs["itemcolorrelative"].setAttribute('style', '')
                this.$nextTick(() => {
                    const opener = this.$refs.itemcolor.$refs.submenu.getBoundingClientRect()
                    const box = this.$refs["itemcolor"].$el.getBoundingClientRect()
                    let left = Math.min(opener.x - box.x - 40, window.innerWidth - (box.right) - 17);
                    let top = Math.min(opener.y - box.y - 16, window.innerHeight - (box.bottom) - 28);
                    this.$refs["itemcolorrelative"].setAttribute('style', 'margin-left:' + left + 'px; margin-top:' + top + 'px')
                })
            })
        },
        round(a, b) {
            if (a) {
                return math.round(a, 0 + b);
            }
            return 0;
        },
        addItem(trigger) {
            this.group.items.push({
                reference: this.newItem.reference ?? this.nextItemReference(),
                defaultReference: this.nextItemReference(),
                description: this.newItem.description,
                quantity: 0,
                uom: '',
                rate: 0,
                total: 0,
                color: '#FFFFFF',
                isNew: true,
            });
            this.$nextTick(() => {
                if (trigger === 'description') {
                    this.$refs.items[this.$refs.items.length - 1].$refs.description.focus()
                } else {
                    this.$refs.items[this.$refs.items.length - 1].$refs.reference.disableSelectAll()
                    this.$refs.items[this.$refs.items.length - 1].$refs.reference.$el.focus()
                    this.$refs.items[this.$refs.items.length - 1].$refs.reference.enableSelectAll()
                }
                this.newItem.reference = null
                this.newItem.description = ''
                this.estimate.update()
                this.estimate.addSnapshot(true)
            })
        },
        nextItemReference() {
            const groupNumber = this.estimate.preliminaries.indexOf(this.group) + 1;
            const itemNumber = this.group.items.length + 1
            return `${groupNumber}.${itemNumber}`
        },
        deleteGroup() {
            this.estimate.preliminaries = this.estimate.preliminaries.filter((group) => group !== this.group)
            this.estimate.update()
            this.estimate.addSnapshot()
        },
        cutGroup() {
            this.clipboardGroupData[0] = cloneDeep(this.group);
            remoteStorage.setItem('estimate-group-clipboard', JSON.stringify(this.clipboardGroupData))
            remoteStorage.setItem('estimate-clipboard-view', 'group');
            this.estimate.preliminaries = this.estimate.preliminaries.filter((group) => group !== this.group)
            this.estimate.update()
            this.estimate.addSnapshot()
            this.$emit('reflow')
        },
        copyGroup() {
            this.clipboardGroupData[0] = cloneDeep(this.group);
            remoteStorage.setItem('estimate-group-clipboard', JSON.stringify(this.clipboardGroupData))
            remoteStorage.setItem('estimate-clipboard-view', 'group');
        },
    },
}
</script>
